// apps/admin-ui/src/pages/Dashboard.tsx
// RBAC-aware Dashboard router - routes to role-specific dashboard
// ALL RIGHTS RESERVED

/**
 * Admin Dashboard
 * Contract-required reporting display per Sections 1.3.4, 1.4.3, 2.3.3
 * 
 * This is the main dashboard router that:
 * 1. Gets the current employee from auth
 * 2. Determines their primary role
 * 3. Gets the layout configuration for that role
 * 4. Renders the appropriate RoleDashboard
 */

import { Navigate } from 'react-router-dom';
import { getCurrentEmployee } from '../utils/auth';
import { getPrimaryRole, type Role } from '../utils/rbac';
import { getRoleLayout } from '../config/dashboardWidgets';
import { RoleDashboard } from '../components/RoleDashboard';

// =============================================================================
// ACCESS DENIED COMPONENT
// =============================================================================

function AccessDenied() {
  return (
    <div className="dashboard-layout empty">
      <h2>Access Denied</h2>
      <p>Your role does not have access to the admin dashboard.</p>
      <p>Please contact your administrator if you believe this is an error.</p>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Dashboard() {
  // Get current employee from localStorage
  const employee = getCurrentEmployee();

  // Redirect to login if not authenticated
  if (!employee) {
    return <Navigate to="/login" replace />;
  }

  // Ensure roles is an array (handle legacy single-role format)
  const roles = Array.isArray(employee.roles) 
    ? employee.roles as Role[]
    : [employee.roles as unknown as Role];

  // Get primary role based on priority
  const role = getPrimaryRole(roles);

  // Get layout configuration for this role
  const layout = getRoleLayout(role);

  // Show access denied if no layout for this role
  if (!layout) {
    return <AccessDenied />;
  }

  // Render the role-specific dashboard
  return (
    <RoleDashboard
      layout={layout}
      role={role}
      employee={{
        id: employee.id,
        name: employee.name,
        email: employee.email,
        roles,
      }}
    />
  );
}

export default Dashboard;
